export type DrumVoice = "kick" | "snare" | "hat" | "ride" | "crash" | "rackTom" | "floorTom";

export class DrumSynth {
  context?: AudioContext;
  master?: GainNode;

  async ready(): Promise<AudioContext> {
    this.context ??= new AudioContext({ sampleRate: 48_000 });
    if (!this.master) {
      this.master = this.context.createGain();
      this.master.gain.value = 0.58;
      const compressor = this.context.createDynamicsCompressor();
      compressor.threshold.value = -18;
      compressor.ratio.value = 4;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.12;
      this.master.connect(compressor).connect(this.context.destination);
    }
    await this.context.resume();
    return this.context;
  }

  async hit(voice: DrumVoice, at?: number, character: "default" | "warm" | "bright" = "default", velocity = 1): Promise<void> {
    const context = await this.ready();
    const time = at ?? context.currentTime;
    const level = Math.max(0.05, Math.min(1, velocity));
    if (voice === "kick" || voice === "rackTom" || voice === "floorTom") this.tonalHit(voice, time, level);
    else this.noiseHit(voice, time, character, level);
  }

  private tonalHit(voice: "kick" | "rackTom" | "floorTom", at: number, velocity: number): void {
    const context = this.context!;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    const start = voice === "kick" ? 145 : voice === "rackTom" ? 285 : 175;
    const end = voice === "kick" ? 47 : voice === "rackTom" ? 145 : 72;
    const pitchDrop = voice === "kick" ? 0.08 : voice === "rackTom" ? 0.11 : 0.18;
    const decay = voice === "kick" ? 0.34 : voice === "rackTom" ? 0.38 : 0.68;
    oscillator.frequency.setValueAtTime(start, at);
    oscillator.frequency.exponentialRampToValueAtTime(end, at + pitchDrop);
    gain.gain.setValueAtTime(velocity, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + decay);
    oscillator.connect(gain).connect(this.master!);
    oscillator.start(at);
    oscillator.stop(at + decay + 0.05);
  }

  private noiseHit(voice: Exclude<DrumVoice, "kick" | "rackTom" | "floorTom">, at: number, character: "default" | "warm" | "bright", velocity: number): void {
    const context = this.context!;
    const duration = voice === "crash" ? 1.8 : voice === "ride" ? 0.8 : voice === "snare" ? character === "bright" ? 0.52 : character === "warm" ? 0.17 : 0.24 : 0.08;
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();
    source.buffer = buffer;
    filter.type = voice === "snare" ? "bandpass" : "highpass";
    filter.frequency.value = voice === "snare"
      ? character === "warm" ? 1050 : character === "bright" ? 3600 : 1750
      : voice === "hat" ? 7200
        : voice === "ride" ? 5200
          : 3000;
    filter.Q.value = voice === "snare" ? 0.7 : voice === "ride" ? 0.85 : 0.35;
    const peak = voice === "hat"
      ? 0.24
      : voice === "snare"
        ? character === "bright" ? 0.48 : character === "warm" ? 0.64 : 0.72
        : voice === "ride" ? 0.28 : 0.4;
    gain.gain.setValueAtTime(peak * velocity, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + duration);
    source.connect(filter).connect(gain).connect(this.master!);
    source.start(at);
    if (voice === "snare") this.snareBody(at, character, velocity);
  }

  private snareBody(at: number, character: "default" | "warm" | "bright", velocity: number): void {
    const context = this.context!;
    const body = context.createOscillator();
    const bodyGain = context.createGain();
    body.type = "triangle";
    body.frequency.setValueAtTime(character === "warm" ? 175 : character === "bright" ? 215 : 195, at);
    bodyGain.gain.setValueAtTime((character === "warm" ? 0.5 : 0.32) * velocity, at);
    bodyGain.gain.exponentialRampToValueAtTime(0.001, at + (character === "warm" ? 0.15 : 0.22));
    body.connect(bodyGain).connect(this.master!);
    body.start(at);
    body.stop(at + 0.24);
    if (character !== "bright") return;
    const ring = context.createOscillator();
    const ringGain = context.createGain();
    ring.type = "sine";
    ring.frequency.setValueAtTime(880, at);
    ringGain.gain.setValueAtTime(0.13 * velocity, at + 0.015);
    ringGain.gain.exponentialRampToValueAtTime(0.001, at + 0.48);
    ring.connect(ringGain).connect(this.master!);
    ring.start(at + 0.015);
    ring.stop(at + 0.5);
  }
}

export const drumSynth = new DrumSynth();
